import { useCallback, useContext, useEffect } from 'react'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import { ModalKeys, ModalStoreType, ModalType, ReportModalType, WindowKeys } from '../utils/scenario-types'
import { ModalModeType } from '../../../CommonComponents/modals/Modal'
import { getReportWindowWidth, REPORT_WINDOWS, WINDOW_HEIGHT, WINDOW_SIZE } from '../utils/constants'

const useReportWindow = () => {
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useReportWindo must be used within a Scenario States Provider')
    }

    const { windowStore, setWindowStore } = context
    useEffect(() => {
        const clickOutside = (e: MouseEvent) => {
            if (e.target instanceof Element) {
                const modalContainers = document.getElementsByClassName('custom__modal__container__wrapper');
                const modalTriggers = document.getElementsByClassName('modal__trigger__button');
                const dropDowns = document.getElementsByClassName('select__container');
                const buttons = document.getElementsByClassName('group__button');

                const totalElements = [...Array.from(modalContainers), ...Array.from(modalTriggers), ...Array.from(dropDowns), ...Array.from(buttons)];
                let clickedInside = false;
                const filterElements = totalElements.filter((element) => {
                    return !element.classList.contains('scenario__create__modal')
                })
                for (let i = 0; i < filterElements.length; i++) {
                    if (filterElements[i].contains(e.target as Node)) {
                        clickedInside = true;
                        break;
                    }
                }

                if (clickedInside) return;
                // closeAllReportModals()
            }
        }

        const canvasClickHandler = (e: any) => {
            const canvas = document.querySelector("canvas");
            if (canvas && canvas.contains(e.target)) {
                clickOutside(e);
            }
        };

        const onEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeAllReportModals()
            }
        }

        document.addEventListener("click", canvasClickHandler);
        document.addEventListener('mousedown', clickOutside)
        document.addEventListener('keydown', onEscape)
        return () => {
            document.removeEventListener('mousedown', clickOutside)
            document.removeEventListener("click", canvasClickHandler);
            document.removeEventListener('keydown', onEscape)
        }
    }, [setWindowStore])


    function getSortedOpenWindows(modals: ReportModalType) {
        const pinnedModals: {
            report: string
            name: string
            zIndex: number
            open: boolean
        }[] = []
        Object.entries(modals).map(([reportName, report]) => {
            pinnedModals.push({
                report: reportName,
                name: reportName,
                zIndex: report.zIndex,
                open: report.open,
            })
            Object.entries(report.windows).map(([windowName, window]) => {
                pinnedModals.push({
                    report: reportName,
                    name: windowName,
                    zIndex: window.zIndex,
                    open: window.open,
                })
            })
        })
        return pinnedModals.filter(w => w.open).sort((a, b) => a.zIndex - b.zIndex)
    }

    const getSortedPinnedWindows = (modals: ReportModalType) => {
        const pinnedModals: {
            report: string
            name: string
            zIndex: number
            pinned: boolean
            right: number
            top: number
        }[] = []
        Object.entries(modals).map(([reportName, report]) => {
            pinnedModals.push({
                report: reportName,
                name: reportName,
                zIndex: report.zIndex,
                pinned: report.pinned,
                right: report.position.right || 0,
                top: report.position.top || 0,
            })
            Object.entries(report.windows).map(([windowName, window]) => {
                pinnedModals.push({
                    report: reportName,
                    name: windowName,
                    zIndex: window.zIndex,
                    pinned: window.pinned,
                    right: window.position.right || 0,
                    top: window.position.top || 0,
                })
            })
        })
        return pinnedModals.filter(w => w.pinned).sort((a, b) => a.right - b.right)
    }

    const closeAllReportModals = useCallback(() => {
        setWindowStore((prev) => {
            const updatedState = Object.fromEntries(
                Object.entries(prev.modals).map(([modalKey, modal]) => {
                    const updatedWindows = Object.fromEntries(
                        Object.entries(modal.windows).map(([windowKey, win]) => {
                            if (win.pinned) return [windowKey, win]
                            return [windowKey, REPORT_WINDOWS[modalKey].windows[windowKey]]
                        })
                    )
                    if (!modal.pinned) return [modalKey, {
                        ...REPORT_WINDOWS[modalKey],
                        windows: updatedWindows
                    }]
                    return [
                        modalKey,
                        { ...modal, windows: updatedWindows },
                    ]
                })
            )
            return {
                ...prev,
                modals: updatedState
            } as ModalStoreType
        })
    }, [setWindowStore])

    const closeReportModalWindows = useCallback((modalKey: ModalKeys) => {
        setWindowStore((prev) => ({
            ...prev,
            modals: {
                ...prev.modals,
                [modalKey]: REPORT_WINDOWS[modalKey],
            },
        }))
    }, [setWindowStore])



    const openReportModal = useCallback((modalKey: ModalKeys, windowKey?: WindowKeys) => {
        setWindowStore((prev) => {
            if (prev.modals[modalKey].pinned || !windowKey) {
                return {
                    ...prev,
                    modals: {
                        ...prev.modals,
                        [modalKey]: {
                            ...prev.modals[modalKey],
                            zIndex: prev.nextZindex,
                        }
                    },
                    nextZindex: prev.nextZindex + 1
                }
            }
            const selected = getAvailableWindows('open', modalKey, windowKey, prev.modals)
            const updatedWindows = {
                ...prev,
                modals: {
                    ...prev.modals,
                    [modalKey]: {
                        ...prev.modals[modalKey],
                        selected: selected,
                        open: !prev.modals[modalKey].open,
                        windowSize: WINDOW_SIZE.MEDIUM,
                        position: REPORT_WINDOWS[modalKey].position,
                        zIndex: prev.nextZindex,
                        windows: prev?.modals[modalKey].windows
                    }
                },
                nextZindex: prev.nextZindex + 1
            }
            return updatedWindows
        })
    }, [setWindowStore])

    const closeReportModal = useCallback((modalType: ModalType, modalKey: ModalKeys, windowKey?: WindowKeys) => {
        setWindowStore((prev) => {
            const updatedWindows = {
                ...prev,
                modals: {
                    ...prev.modals,
                    [modalKey]: (modalType === 'child' && windowKey) ? {
                        ...prev.modals[modalKey],
                        windows: {
                            ...prev.modals[modalKey].windows,
                            [windowKey]: REPORT_WINDOWS[modalKey].windows[windowKey]
                        }
                    } : {
                        ...REPORT_WINDOWS[modalKey],
                        windows: prev?.modals[modalKey].windows
                    }
                },
            }
            return updatedWindows
        })
    }, [setWindowStore])

    const pinReportModal = useCallback((modalType: ModalType, modalKey: ModalKeys, windowKey?: WindowKeys) => {
        setWindowStore((prev) => {
            const allPinnedWindows = getSortedPinnedWindows(prev.modals)
            const windowPosition = {
                height: WINDOW_HEIGHT.MEDIUM,
                width: REPORT_WINDOWS[modalKey].position.width,
                right: allPinnedWindows?.length === 0 ? 10 : (allPinnedWindows[allPinnedWindows.length - 1].right || 0) + 40,
                top: allPinnedWindows?.length === 0 ? 10 : (allPinnedWindows[allPinnedWindows.length - 1].top || 0) + 50,
            }

            const updatedWindows = {
                ...prev,
                modals: {
                    ...prev.modals,
                    [modalKey]: {
                        ...prev.modals[modalKey],
                        pinned: modalType === 'root' ? !prev.modals[modalKey].pinned : prev.modals[modalKey].pinned,
                        windowSize: modalType === 'root' ? WINDOW_SIZE.MEDIUM : prev.modals[modalKey].windowSize,
                        position: modalType === 'root' ? prev.modals[modalKey].pinned ? REPORT_WINDOWS[modalKey].position : windowPosition : prev.modals[modalKey].position,
                        zIndex: modalType === 'root' ? prev.nextZindex : prev.modals[modalKey].zIndex,
                        windows: (modalType === 'child' && windowKey) ? {
                            ...prev.modals[modalKey].windows,
                            [windowKey]: {
                                ...prev.modals[modalKey].windows[windowKey],
                                pinned: !prev.modals[modalKey].windows[windowKey].pinned,
                                windowSize: WINDOW_SIZE.MEDIUM,
                                position: prev.modals[modalKey].windows[windowKey].pinned ? REPORT_WINDOWS[modalKey].windows[windowKey].position : windowPosition,
                                zIndex: prev.nextZindex
                            }
                        } : prev.modals[modalKey].windows,
                    },
                },
                nextZindex: prev.nextZindex + 1
            }
            return updatedWindows
        })
    }, [setWindowStore])

    const focusReportModal = useCallback((modalType: ModalType, modalKey: ModalKeys, windowKey?: WindowKeys) => {
        setWindowStore((prev) => {
            const updatedWindows = {
                ...prev,
                modals: {
                    ...prev.modals,
                    [modalKey]: {
                        ...prev.modals[modalKey],
                        zIndex: modalType === 'root' ? prev.nextZindex : prev.modals[modalKey].zIndex,
                        windows: (modalType === 'child' && windowKey) ? {
                            ...prev.modals[modalKey].windows,
                            [windowKey]: {
                                ...prev.modals[modalKey].windows[windowKey],
                                zIndex: prev.nextZindex
                            }
                        } : prev.modals[modalKey].windows,
                    }
                },
                nextZindex: prev.nextZindex + 1
            }
            return updatedWindows
        })
    }, [setWindowStore])

    const getAvailableWindows = useCallback((mode: 'open' | 'detached', modalKey: ModalKeys, windowKey: WindowKeys, modals: ReportModalType) => {
        switch (modalKey) {
            case 'orbitReport':
                const availableOrbitalWindows: string[] = []
                Object.entries(modals[modalKey].windows).filter(([key, value]) => {
                    if (key !== windowKey && !value.detached) {
                        availableOrbitalWindows.push(key)
                    } else if (key === windowKey && mode === 'open' && !value.detached) {
                        availableOrbitalWindows.push(key)
                    }
                })

                return availableOrbitalWindows.includes(windowKey) ? windowKey : availableOrbitalWindows[0] || 'exexposure'
            case 'presimReport':
                const availablePresimWindows: string[] = []
                Object.entries(modals[modalKey].windows).filter(([key, value]) => {
                    if (key !== windowKey && !value.detached) {
                        availablePresimWindows.push(key)
                    } else if (key === windowKey && mode === 'open' && !value.detached) {
                        availablePresimWindows.push(key)
                    }
                })
                return availablePresimWindows.includes(windowKey) ? windowKey : availablePresimWindows[0] || 'power'
            case 'faultReport':
                return 'faults'
            case 'scheduleReport':
                return 'schedule'
            default:
                return windowKey
        }
    }, [])

    const detachedWindow = (modalKey: ModalKeys, windowKey?: WindowKeys) => {
        setWindowStore((prev) => {
            if (!windowKey) return prev
            const selected = getAvailableWindows('detached', modalKey, windowKey, prev.modals)
            const updatedWindows = {
                ...prev,
                modals: {
                    ...prev.modals,
                    [modalKey]: {
                        ...prev.modals[modalKey],
                        selected: selected,
                        windows: {
                            ...prev.modals[modalKey].windows,
                            [windowKey]: {
                                ...prev.modals[modalKey].windows[windowKey],
                                open: true,
                                detached: true,
                                zIndex: prev.nextZindex
                            }
                        },
                    }
                },
                nextZindex: prev.nextZindex + 1
            }
            return updatedWindows
        })
    }

    const resizeModal = (modalType: ModalType, modalKey: ModalKeys, windowKey?: WindowKeys) => {
        setWindowStore((prev) => {
            const size = (modalType === 'child' && windowKey) ? prev.modals[modalKey].windows[windowKey].windowSize : prev.modals[modalKey].windowSize
            const height = size === WINDOW_SIZE.SMALL ? WINDOW_HEIGHT.MEDIUM : size === WINDOW_SIZE.MEDIUM ? WINDOW_HEIGHT.LARGE : WINDOW_HEIGHT.SMALL
            const updatedSize = size === WINDOW_SIZE.SMALL ? WINDOW_SIZE.MEDIUM : size === WINDOW_SIZE.MEDIUM ? WINDOW_SIZE.LARGE : WINDOW_SIZE.SMALL
            const width = getReportWindowWidth(modalKey, updatedSize)
            const updatedWindows = {
                ...prev,
                modals: {
                    ...prev.modals,
                    [modalKey]: {
                        ...prev.modals[modalKey],
                        position: modalType === 'root' ? {
                            ...prev.modals[modalKey].position,
                            height: height,
                            width: width
                        } : prev.modals[modalKey].position,
                        windowSize: modalType === 'root' ? updatedSize : prev.modals[modalKey].windowSize,
                        windows: (modalType === 'child' && windowKey) ? {
                            ...prev.modals[modalKey].windows,
                            [windowKey]: {
                                ...prev.modals[modalKey].windows[windowKey],
                                windowSize: updatedSize,
                                position: {
                                    ...prev.modals[modalKey].windows[windowKey].position,
                                    height: height,
                                    width: width
                                }
                            }
                        } : prev.modals[modalKey].windows,
                    }
                },
            }
            return updatedWindows
        })
    }

    const onSelectTab = (modalKey: ModalKeys, windowKey?: WindowKeys) => {
        if (!windowKey) return
        setWindowStore((prev) => {
            const updatedWindows = {
                ...prev,
                modals: {
                    ...prev.modals,
                    [modalKey]: {
                        ...prev.modals[modalKey],
                        selected: windowKey,
                    }
                },
            }
            return updatedWindows
        })
    }

    const onReportModalChange = (event: any, modalType: ModalType, modalKey: ModalKeys, mode: ModalModeType, windowKey?: WindowKeys) => {
        event.stopPropagation()
        switch (mode) {
            case "open":
                openReportModal(modalKey, windowKey)
                break;
            case "close":
                closeReportModal(modalType, modalKey, windowKey)
                break;
            case "pinned":
                pinReportModal(modalType, modalKey, windowKey)
                break;
            case "focus":
                focusReportModal(modalType, modalKey, windowKey)
                break;
            case "detached":
                detachedWindow(modalKey, windowKey)
                break;
            case "resize":
                resizeModal(modalType, modalKey, windowKey)
                break;
            case "select_tab":
                onSelectTab(modalKey, windowKey)
                break;
            default:
                break;
        }
    }

    return { windowStore, closeAllReportModals, closeReportModalWindows, openReportModal, closeReportModal, pinReportModal, focusReportModal, onReportModalChange, getSortedPinnedWindows, getSortedOpenWindows }
}


export default useReportWindow
